import { useAppStatusIncidents } from '@src/main/modules/app_status/application/useAppStatusIncidents';
import { AppStatusIncident } from '@src/main/modules/app_status/domain/appStatusIncident.model';
import AppStatusAuditInfo from '@src/main/modules/app_status/ui/components/AppStatusAuditInfo';
import AppStatusIncidentsTable from '@src/main/modules/app_status/ui/components/AppStatusIncidentsTable';
import ImageDropzone from '@src/main/modules/app_status/ui/components/ImageDropzone';
import {
  AppStatusFilterMeta,
  mapAppStatusFiltersToCriteria,
} from '@src/main/modules/app_status/ui/filters/mapAppStatusFiltersToCriteria';
import { useAppStatusFilters } from '@src/main/modules/app_status/ui/hooks/useAppStatusFilters';
import { useAppStatusOptions } from '@src/main/modules/app_status/ui/hooks/useAppStatusOptions';
import {
  getAppStatusIncidentSchema,
  maskAppVersionInput,
} from '@src/main/modules/app_status/ui/schema/appStatusIncident.schema';
import { emptyIncident } from '@src/main/modules/app_status/ui/utils/appStatusIncidents.utils';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useToast } from '@src/main/shared/context/ToastContext';
import ContentCard from '@src/main/shared/ui/components/card/ContentCard';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import styles from './AppStatusIncidents.module.scss';

const AppStatusIncidents: FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const { severityOptions, statusOptions } = useAppStatusOptions();
  const initialFilters = useAppStatusFilters();
  const [filters, setFilters] = useState<AppStatusFilterMeta>(initialFilters);
  const [incidentForm, setIncidentForm] = useState<AppStatusIncident | null>(null);
  const [incidentToDelete, setIncidentToDelete] = useState<AppStatusIncident | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const criteria = useMemo(() => mapAppStatusFiltersToCriteria(filters), [filters]);

  const {
    createIncident,
    deleteIncident,
    handlePage,
    handleSort,
    incidents,
    isFetching,
    isLoading,
    isSaving,
    pageNumber,
    pageSize,
    refetch,
    resetPage,
    sortField,
    sortOrder,
    total,
    updateIncident,
  } = useAppStatusIncidents(criteria);

  const canEdit = hasPermission(PERMISSIONS.APP_STATUS.edit);

  const updateIncidentForm = <K extends keyof AppStatusIncident>(
    field: K,
    value: AppStatusIncident[K],
  ) => {
    setIncidentForm((current) => (current ? { ...current, [field]: value } : current));
    setValidationErrors((current) => ({ ...current, [field]: '' }));
  };

  const closeForm = () => {
    if (isSaving) return;
    setIncidentForm(null);
    setValidationErrors({});
  };

  const handleSave = async () => {
    if (!incidentForm) return;

    try {
      const validated = getAppStatusIncidentSchema(t).parse(incidentForm);
      const input = {
        date: validated.date,
        errorDescription: validated.errorDescription,
        errorDescriptionImage: validated.errorDescriptionImage,
        possibleSolution: validated.possibleSolution,
        possibleSolutionImage: validated.possibleSolutionImage,
        public: validated.public,
        visibleApp: validated.visibleApp,
        severity: validated.severity,
        status: validated.status,
        summary: validated.summary,
        title: validated.title,
        version: validated.version,
      };

      if (incidentForm.id) {
        await updateIncident({ id: incidentForm.id, input });
        showSuccessMessage(toast, t('appStatus.updatedSuccess'));
      } else {
        await createIncident(input);
        showSuccessMessage(toast, t('appStatus.createdSuccess'));
      }
      closeForm();
    } catch (error) {
      if (error instanceof z.ZodError) {
        setValidationErrors(
          Object.fromEntries(error.errors.map((item) => [String(item.path[0]), item.message])),
        );
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const confirmDelete = async () => {
    if (!incidentToDelete || isSaving) return;
    try {
      await deleteIncident(incidentToDelete.id);
      showSuccessMessage(toast, t('appStatus.deletedSuccess'));
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setIncidentToDelete(null);
    }
  };

  return (
    <ContentCard>
      <HeaderTitle title={t('appStatus.title')} />
      <TableHeader
        onCreate={() => setIncidentForm(emptyIncident())}
        onRefresh={() => refetch()}
        visibility={[false, canEdit, false, true, false, false]}
      />
      <AppStatusIncidentsTable
        canEdit={canEdit}
        filters={filters}
        first={pageNumber * pageSize}
        incidents={incidents}
        isLoading={isLoading || isFetching}
        onDelete={setIncidentToDelete}
        onEdit={(incident) => setIncidentForm({ ...incident })}
        onFilter={(nextFilters) => {
          setFilters(nextFilters);
          resetPage();
        }}
        onPage={handlePage}
        onSort={handleSort}
        pageSize={pageSize}
        sortField={sortField}
        sortOrder={sortOrder}
        total={total}
      />

      <AppDialog
        visible={!!incidentForm}
        title={incidentForm?.id ? t('appStatus.updateIncident') : t('appStatus.createIncident')}
        onClose={closeForm}
        onConfirm={handleSave}
        disableButtons={isSaving}
        style={{ width: '80vw', height: '80vh' }}
        breakpoints={{ '960px': '90vw', '640px': '95vw' }}
      >
        {incidentForm && (
          <div className={styles.form}>
            <div className={styles.field}>
              <span className="p-float-label">
                <InputText id="app-status-code" value={incidentForm.code} disabled />
                <label htmlFor="app-status-code">{t('appStatus.columns.code')}</label>
              </span>
            </div>
            <div className={styles.field}>
              <span className="p-float-label">
                <InputText
                  id="app-status-date"
                  value={incidentForm.date}
                  onChange={(event) => updateIncidentForm('date', event.target.value)}
                />
                <label htmlFor="app-status-date">{t('appStatus.columns.date')}</label>
              </span>
              {validationErrors.date && <small>{validationErrors.date}</small>}
            </div>
            <div className={styles.field}>
              <span className="p-float-label">
                <InputText
                  id="app-status-version"
                  value={incidentForm.version ?? ''}
                  maxLength={50}
                  placeholder="4.1.1 / 4.1.2.B"
                  onChange={(event) => {
                    const maskedVersion = maskAppVersionInput(event.target.value);
                    if (maskedVersion !== null) updateIncidentForm('version', maskedVersion);
                  }}
                />
                <label htmlFor="app-status-version">{t('appStatus.form.versionOptional')}</label>
              </span>
              {validationErrors.version && <small>{validationErrors.version}</small>}
            </div>
            <div className={styles.field}>
              <span className="p-float-label">
                <Dropdown
                  id="app-status-severity"
                  value={incidentForm.severity}
                  options={severityOptions}
                  onChange={(event) => updateIncidentForm('severity', event.value)}
                />
                <label htmlFor="app-status-severity">{t('appStatus.columns.severity')}</label>
              </span>
            </div>
            <div className={styles.field}>
              <span className="p-float-label">
                <Dropdown
                  id="app-status-status"
                  value={incidentForm.status}
                  options={statusOptions}
                  onChange={(event) => updateIncidentForm('status', event.value)}
                />
                <label htmlFor="app-status-status">{t('appStatus.columns.status')}</label>
              </span>
            </div>
            {(['title', 'summary'] as const).map((field) => (
              <div className={styles.field} key={field}>
                <span className="p-float-label">
                  <InputText
                    id={`app-status-${field}`}
                    value={incidentForm[field]}
                    onChange={(event) => updateIncidentForm(field, event.target.value)}
                  />
                  <label htmlFor={`app-status-${field}`}>{t(`appStatus.columns.${field}`)}</label>
                </span>
                {validationErrors[field] && <small>{validationErrors[field]}</small>}
              </div>
            ))}
            <div className={styles['details-grid']}>
              <div className={styles['detail-column']}>
                <div className={styles.field}>
                  <span className="p-float-label">
                    <InputTextarea
                      id="app-status-errorDescription"
                      value={incidentForm.errorDescription}
                      rows={8}
                      onChange={(event) =>
                        updateIncidentForm('errorDescription', event.target.value)
                      }
                    />
                    <label htmlFor="app-status-errorDescription">
                      {t('appStatus.columns.errorDescription')}
                    </label>
                  </span>
                  {validationErrors.errorDescription && (
                    <small>{validationErrors.errorDescription}</small>
                  )}
                </div>
                <ImageDropzone
                  id="app-status-errorDescriptionImage"
                  label={t('appStatus.form.errorDescriptionImage')}
                  value={incidentForm.errorDescriptionImage}
                  existingUrl={incidentForm.errorDescriptionImageUrl}
                  onFileSelect={(file) => updateIncidentForm('errorDescriptionImage', file)}
                />
              </div>
              <div className={styles['detail-column']}>
                <div className={styles.field}>
                  <span className="p-float-label">
                    <InputTextarea
                      id="app-status-possibleSolution"
                      value={incidentForm.possibleSolution}
                      rows={8}
                      onChange={(event) =>
                        updateIncidentForm('possibleSolution', event.target.value)
                      }
                    />
                    <label htmlFor="app-status-possibleSolution">
                      {t('appStatus.columns.possibleSolution')}
                    </label>
                  </span>
                  {validationErrors.possibleSolution && (
                    <small>{validationErrors.possibleSolution}</small>
                  )}
                </div>
                <ImageDropzone
                  id="app-status-possibleSolutionImage"
                  label={t('appStatus.form.possibleSolutionImage')}
                  value={incidentForm.possibleSolutionImage}
                  existingUrl={incidentForm.possibleSolutionImageUrl}
                  onFileSelect={(file) => updateIncidentForm('possibleSolutionImage', file)}
                />
              </div>
            </div>
            <div className={styles['checkbox-group']}>
              <label className={styles.checkbox} htmlFor="app-status-public">
                <Checkbox
                  inputId="app-status-public"
                  checked={incidentForm.public}
                  onChange={(event) => updateIncidentForm('public', Boolean(event.checked))}
                />
                <span>{t('appStatus.columns.public')}</span>
              </label>
              <label className={styles.checkbox} htmlFor="app-status-visibleApp">
                <Checkbox
                  inputId="app-status-visibleApp"
                  checked={incidentForm.visibleApp}
                  onChange={(event) => updateIncidentForm('visibleApp', Boolean(event.checked))}
                />
                <span>{t('appStatus.columns.visibleApp')}</span>
              </label>
            </div>
            {incidentForm.id && incidentForm.auditFields && (
              <AppStatusAuditInfo auditFields={incidentForm.auditFields} />
            )}
          </div>
        )}
      </AppDialog>

      <ConfirmDeleteDialog
        visible={!!incidentToDelete}
        message={t('appStatus.confirmDelete', { code: incidentToDelete?.code })}
        confirmDelete={confirmDelete}
        rejectDelete={() => setIncidentToDelete(null)}
      />
    </ContentCard>
  );
};

export default AppStatusIncidents;
